import React, { useState } from 'react';
import { Sparkles, Target, Users, Shield, Rocket, Award, Copy, FileText, X, Check, ExternalLink } from 'lucide-react';

const About: React.FC = () => {
  const [showWhitepaper, setShowWhitepaper] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const contractAddress = 'ALDNwmYLE8dK19PPgmWA4gSpFzvRozsG8y2J48SL6Jpo';

  const features = [
    {
      icon: Shield,
      title: 'Security First',
      description: 'Audited smart contracts and multi-sig treasury for maximum security',
    },
    {
      icon: Users,
      title: 'Community Driven',
      description: 'Governance tokens give our community a voice in platform decisions',
    },
    {
      icon: Rocket,
      title: 'Innovation Hub',
      description: 'Cutting-edge tools for token creation, bundling, and sniping',
    },
    {
      icon: Award,
      title: 'Trusted Platform',
      description: 'Established reputation with successful launches and happy users',
    },
  ];

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(contractAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const WhitepaperModal = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-2xl font-bold flex items-center">
            <FileText className="w-6 h-6 mr-2 text-purple-400" />
            Aladdin Whitepaper
          </h2>
          <button
            onClick={() => setShowWhitepaper(false)}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
          <div className="prose prose-invert max-w-none">
            <h1 className="text-3xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Aladdin: The Solana-Based Token Launchpad
            </h1>
            
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-purple-400">1. Abstract</h2>
              <p className="text-gray-300 leading-relaxed">
                Aladdin is a decentralized launchpad built on the Solana blockchain, designed to
                streamline the creation, distribution, and launch of tokens. With built-in bundling
                tools, sniping functionality, transparent revenue sharing, and Solana's high-speed,
                low-fee infrastructure, Aladdin empowers developers, creators, and communities to
                deploy tokens efficiently and securely. This whitepaper outlines the core functionality,
                ecosystem, and tokenomics of the ALADDIN token.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-purple-400">2. Introduction</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Token creation has traditionally been a fragmented and technically complex
                process, requiring separate tools for deployment, liquidity, marketing, and
                community engagement. Aladdin solves this by offering an all-in-one token launch
                solution on Solana, enabling rapid deployment with transparency and fairness.
              </p>
              <h3 className="text-xl font-semibold mb-2 text-pink-400">Why Solana?</h3>
              <p className="text-gray-300 leading-relaxed">
                Solana provides unmatched scalability, with transaction speeds up to 65,000 TPS
                and negligible fees. This makes it ideal for token launches that require speed,
                responsiveness, and user cost-efficiency.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-purple-400">3. Platform Features</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-2 text-pink-400">3.1 Token Creation & Launch</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Aladdin allows creators to deploy custom tokens within minutes, including setting
                    parameters like supply, vesting, liquidity, and sale rounds. No code required.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2 text-pink-400">3.2 Advanced Bundling</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Projects can structure multiple sale phases (e.g., private, presale, public) and token
                    utility functions using advanced bundling tools, allowing full customization and easy
                    automation.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2 text-pink-400">3.3 Sniping Tools</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Sniping tools allow users and investors to monitor upcoming launches, auto-detect
                    drops, and react instantly — leveling the playing field during launches.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2 text-pink-400">3.4 Transparent Revenue Sharing</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Aladdin implements an open RevShare model where earnings from launches are
                    transparently distributed between stakeholders (creators, platform, community
                    incentives).
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2 text-pink-400">3.5 Liquidity Launching</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Integrated with Raydium DEX, Aladdin automates initial liquidity provision, ensuring a
                    seamless launch with deep liquidity from day one.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2 text-pink-400">3.6 Ecosystem Partnerships</h3>
                  <p className="text-gray-300 leading-relaxed">
                    The platform supports strategic collaborations, allowing partners to co-launch,
                    co-brand, or integrate projects into the broader Aladdin ecosystem.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-purple-400">4. The ALADDIN Token</h2>
              <h3 className="text-xl font-semibold mb-2 text-pink-400">4.1 Overview</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                The native token of the Aladdin platform is ALADDIN, serving as a utility and
                governance asset within the ecosystem.
              </p>
              <h4 className="text-lg font-semibold mb-2 text-gray-200">Use Cases:</h4>
              <ul className="list-disc list-inside text-gray-300 space-y-1">
                <li>Pay launchpad fees</li>
                <li>Access premium sniping tools</li>
                <li>Participate in governance votes</li>
                <li>Earn revenue share</li>
                <li>Access whitelist tiers for upcoming launches</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-purple-400">5. Tokenomics</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-600">
                  <thead>
                    <tr className="bg-gray-800">
                      <th className="border border-gray-600 p-3 text-left">Category</th>
                      <th className="border border-gray-600 p-3 text-left">Allocation %</th>
                      <th className="border border-gray-600 p-3 text-left">Tokens Allocated</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-600 p-3">Presale (Public & Private)</td>
                      <td className="border border-gray-600 p-3">12.5%</td>
                      <td className="border border-gray-600 p-3">125,000,000 ALADDIN</td>
                    </tr>
                    <tr className="bg-gray-800/50">
                      <td className="border border-gray-600 p-3">Team</td>
                      <td className="border border-gray-600 p-3">17.5%</td>
                      <td className="border border-gray-600 p-3">175,000,000 ALADDIN</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-600 p-3">Reserves (Locked for Future Exchanges)</td>
                      <td className="border border-gray-600 p-3">10%</td>
                      <td className="border border-gray-600 p-3">100,000,000 ALADDIN</td>
                    </tr>
                    <tr className="bg-gray-800/50">
                      <td className="border border-gray-600 p-3">Liquidity (Initial Raydium LP)</td>
                      <td className="border border-gray-600 p-3">40%</td>
                      <td className="border border-gray-600 p-3">400,000,000 ALADDIN</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-600 p-3">Ecosystem Partnerships</td>
                      <td className="border border-gray-600 p-3">10%</td>
                      <td className="border border-gray-600 p-3">100,000,000 ALADDIN</td>
                    </tr>
                    <tr className="bg-gray-800/50">
                      <td className="border border-gray-600 p-3">Marketing</td>
                      <td className="border border-gray-600 p-3">5%</td>
                      <td className="border border-gray-600 p-3">50,000,000 ALADDIN</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-600 p-3">Treasury</td>
                      <td className="border border-gray-600 p-3">5%</td>
                      <td className="border border-gray-600 p-3">50,000,000 ALADDIN</td>
                    </tr>
                    <tr className="bg-gray-800 font-semibold">
                      <td className="border border-gray-600 p-3">Total Supply</td>
                      <td className="border border-gray-600 p-3">100%</td>
                      <td className="border border-gray-600 p-3">1,000,000,000 ALADDIN</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-purple-400 mt-4">
                PS: 1% holders will earn additional revenue from platform fees
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-purple-400">6. Roadmap</h2>
              <div className="space-y-4">
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="text-lg font-semibold text-pink-400">Q3 2025</h3>
                  <p className="text-gray-300">Website & Launchpad beta release, token creation tool live, Token Presale, initial partnerships and DEX listing</p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="text-lg font-semibold text-pink-400">Q1 2026</h3>
                  <p className="text-gray-300">Ecosystem expansion, sniping tool release</p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="text-lg font-semibold text-pink-400">Q2 2026</h3>
                  <p className="text-gray-300">Governance module, staking features</p>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <h3 className="text-lg font-semibold text-pink-400">Future</h3>
                  <p className="text-gray-300">Cross-chain token launches, AI-assisted project vetting</p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-purple-400">7. Security & Audits</h2>
              <p className="text-gray-300 leading-relaxed">
                Security is foundational. All smart contracts will undergo rigorous auditing from
                reputable third-party firms. The platform will implement on-chain verifiability,
                liquidity lock to protect users and projects.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-purple-400">8. Governance</h2>
              <p className="text-gray-300 leading-relaxed mb-3">ALADDIN token holders will have the power to:</p>
              <ul className="list-disc list-inside text-gray-300 space-y-1">
                <li>Vote on project listings</li>
                <li>Adjust launchpad fees</li>
                <li>Propose platform improvements</li>
                <li>Allocate treasury funds for ecosystem growth</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-purple-400">9. Conclusion</h2>
              <p className="text-gray-300 leading-relaxed">
                Aladdin is a next-generation launchpad built to remove friction, empower innovation,
                and support the growth of the Solana ecosystem. Whether you're launching your own
                token or looking to support the next breakout project, Aladdin gives you the tools to
                make magic happen.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-purple-400">10. Disclaimer</h2>
              <p className="text-gray-300 leading-relaxed text-sm">
                This document is not financial advice. Participation in cryptocurrency projects
                involves risk. Ensure you conduct your own research before making any investment
                decisions.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <section id="about" className="py-4 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            {/* Left side - Content */}
            <div className="order-2 lg:order-1">
              <div className="mb-6">
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  About{' '}
                  <span 
                  className="font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent" 
                  style={{ fontFamily: 'Cinzel, serif' }}
                >
                  Aladdin
                </span>
                </h2>
                <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full mb-6"></div>
              </div>
              
              <div className="space-y-4 text-gray-300 mb-8">
                <p className="text-lg leading-relaxed">
                  Aladdin is Solana's premier token launchpad, empowering creators and investors 
                  with the most advanced tools in DeFi. Our platform combines the magic of 
                  seamless token creation with powerful trading tools.
                </p>
                <p className="text-lg leading-relaxed">
                  Built by DeFi veterans and backed by a passionate community, we're on a mission 
                  to democratize token launches and make advanced trading tools accessible to everyone.
                </p>
                <p className="text-lg leading-relaxed">
                  With our innovative RevShare model, bundler tools, and sniper bot capabilities, 
                  Aladdin isn't just a launchpad – it's your gateway to the future of decentralized finance.
                </p>
              </div>

              {/* Contract Address */}
              <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700 mb-6">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <Shield className="w-5 h-5 mr-2 text-purple-400" />
                  Official Contract Address
                </h3>
                <div className="flex items-center justify-between bg-gray-900/50 rounded-lg p-3">
                  <code className="text-sm font-mono text-purple-300 flex-1 break-all">
                    {contractAddress}
                  </code>
                  <button
                    onClick={copyToClipboard}
                    className="ml-3 p-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors flex items-center space-x-1"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {copied && (
                  <p className="text-green-400 text-sm mt-2">Contract address copied to clipboard!</p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setShowWhitepaper(true)}
                  className="flex items-center justify-center px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-medium hover:opacity-90 transition-opacity"
                >
                  <FileText className="w-5 h-5 mr-2" />
                  Read Whitepaper
                </button>
                <a
                  href={`https://revshare.dev/token/ALDNwmYLE8dK19PPgmWA4gSpFzvRozsG8y2J48SL6Jpo`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors"
                >
                  <ExternalLink className="w-5 h-5 mr-2" />
                  View on RevShare
                </a>
              </div>
            </div>

            {/* Right side - Image with rounded corners */}
            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="flex items-center justify-center h-[200px] sm:h-[300px] lg:h-[400px]">
                  <img 
                    src="aladdin.png" 
                    alt="Aladdin Platform" 
                    className="w-full h-full object-contain rounded-2xl"
                    onError={(e) => {
                      // Fallback if image doesn't exist
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.parentElement!.innerHTML = `
                        <div class="text-center scale-75 sm:scale-90 lg:scale-100">
                          <div class="text-6xl sm:text-7xl lg:text-8xl mb-2 sm:mb-4">🧞‍♂️</div>
                          <div class="text-5xl sm:text-6xl mb-2 sm:mb-4">🪔</div>
                          <div class="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent hidden sm:block">
                            Your Wish is Our Command
                          </div>
                        </div>
                      `;
                    }}
                  />
                </div>

                {/* Floating elements */}
                <div className="absolute -top-2 -left-2 lg:-top-4 lg:-left-4 w-12 h-12 lg:w-20 lg:h-20 bg-purple-600/20 rounded-full blur-xl animate-pulse"></div>
                <div className="absolute -bottom-2 -right-2 lg:-bottom-4 lg:-right-4 w-20 h-20 lg:w-32 lg:h-32 bg-pink-600/20 rounded-full blur-xl animate-pulse delay-1000"></div>
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700 hover:border-purple-500 transition-all group">
                  <Icon className="w-10 h-10 text-purple-400 mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Whitepaper Modal */}
      {showWhitepaper && <WhitepaperModal />}
    </>
  );
};

export default About;